import { useContext, useEffect, useRef } from "react";

import { DocumentEventsContext, type EventHandler } from "./DocumentEventsProvider";

/**
 * Subscribe to events on an AppSync channel.
 *
 * The handler is captured in a ref so it can be updated each render without
 * re-opening the subscription. The channel string is the only reactive dep.
 *
 * @param channel  AppSync channel path (e.g. "documents/{caseId}/{userId}").
 *                 No leading slash — matches AWS HTTP publish and Amplify connect.
 * @param handler  Called with the unwrapped payload for every event.
 */
export const useDocumentEvents = (channel: string | undefined, handler: EventHandler): void => {
  const subscribe = useContext(DocumentEventsContext);

  // Keep handler in a ref so the subscription doesn't need to re-open when
  // the caller's closure changes on every render.
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    if (!channel || !subscribe) return;

    return subscribe(channel, (payload) => handlerRef.current(payload));
  }, [channel, subscribe]);
};
